'use client'

import Image from 'next/image'
import { useTranslations } from 'next-intl'
import { useOpenStatusLabel } from './OpenStatusBadge'
import type { OpenStatus } from '@/lib/hours/openStatus'

type Props = {
  businessName: string
  tagline?: string
  heroImageUrl?: string
  heroVideoUrl?: string
  city?: string | null
  pickupWaitMinutes?: number
  readyInLabel?: string
  onViewMenu: () => void
  onDelivery?: () => void
  hasDelivery?: boolean
  openStatus?: OpenStatus | null
}

/**
 * Hero del tema "sumi" (zen minimal): papel washi, tinta y un solo gesto de
 * color — el sello cuadrado bermellón junto al nombre (hanko). Aire vertical
 * extremo, reglas hairline como única ornamentación, CTA rectangular y foto
 * enmarcada con hairline SIN sombra (el tema no usa sombras). La barra
 * superior usa modo sólido (navSolid). Lee tokens del tema (--ms-*).
 */
export function HeroSumi({
  businessName,
  tagline,
  heroImageUrl,
  heroVideoUrl,
  city,
  pickupWaitMinutes,
  readyInLabel,
  onViewMenu,
  onDelivery,
  hasDelivery,
  openStatus,
}: Props) {
  const t = useTranslations('public.microsite')
  const statusLabel = useOpenStatusLabel(openStatus ?? null)
  const hasMedia = !!(heroImageUrl || heroVideoUrl)

  return (
    <section className="w-full" style={{ background: 'var(--ms-bg)' }}>
      {/* pt generoso: el aire ES el tema. Despeja además la barra fija (navSolid). */}
      <div className="mx-auto max-w-3xl px-6 sm:px-8 pt-28 sm:pt-36 pb-14 text-center">
        {city && (
          <div
            className="text-[11px] font-medium uppercase tracking-[0.34em] pl-[0.34em] mb-8"
            style={{ color: 'var(--ms-text-muted)' }}
          >
            {city}
          </div>
        )}

        {/* Nombre + sello hanko */}
        <h1
          className="font-semibold leading-[1.06] tracking-[0.01em] text-[clamp(38px,6.8vw,68px)]"
          style={{ fontFamily: 'var(--ms-font-display)', color: 'var(--ms-text)' }}
        >
          {businessName}
          <span
            className="inline-block w-[0.17em] h-[0.17em] ml-[0.22em] align-baseline"
            style={{ background: 'var(--ms-accent)' }}
            aria-hidden
          />
        </h1>

        {/* Regla hairline — única ornamentación */}
        <div className="mx-auto mt-8 h-px w-14" style={{ background: 'var(--ms-border)' }} aria-hidden />

        {tagline && (
          <p className="mt-7 max-w-md mx-auto text-[15.5px] leading-[1.9]" style={{ color: 'var(--ms-text-soft)' }}>
            {tagline}
          </p>
        )}

        {/* Meta en una sola línea serena */}
        <div
          className="mt-7 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-[12.5px] tracking-wide"
          style={{ color: 'var(--ms-text-muted)' }}
        >
          {statusLabel && (
            <span className="inline-flex items-center gap-2">
              <span
                className="w-[5px] h-[5px] rounded-full"
                style={{
                  background:
                    statusLabel.tone === 'open'
                      ? '#3a9e6e'
                      : statusLabel.tone === 'soon'
                        ? '#f59e0b'
                        : 'var(--ms-text-muted)',
                }}
              />
              {statusLabel.text}
            </span>
          )}
          {statusLabel && <span aria-hidden>·</span>}
          <span>{t('pickupAvailable')}</span>
          {pickupWaitMinutes ? (
            <>
              <span aria-hidden>·</span>
              <span>{readyInLabel ?? t('readyIn', { minutes: pickupWaitMinutes })}</span>
            </>
          ) : null}
        </div>

        {/* CTAs rectangulares */}
        <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
          <button
            type="button"
            onClick={onViewMenu}
            className="inline-flex items-center px-9 py-3.5 text-[13px] font-semibold uppercase tracking-[0.16em] transition hover:brightness-110 active:scale-[0.985]"
            style={{ background: 'var(--ms-accent)', color: '#ffffff', borderRadius: 'var(--ms-radius)' }}
          >
            {t('orderOnline')}
          </button>
          {hasDelivery && onDelivery && (
            <button
              type="button"
              onClick={onDelivery}
              className="inline-flex items-center px-7 py-3.5 text-[13px] font-semibold uppercase tracking-[0.16em] transition hover:bg-black/[0.04]"
              style={{
                color: 'var(--ms-text)',
                border: '1px solid var(--ms-text)',
                borderRadius: 'var(--ms-radius)',
              }}
            >
              {t('delivery')}
            </button>
          )}
        </div>
      </div>

      {/* Foto enmarcada a hairline, sin sombra */}
      {hasMedia && (
        <div className="mx-auto max-w-5xl px-6 sm:px-8 pb-20">
          <div
            className="relative w-full overflow-hidden"
            style={{
              aspectRatio: '16 / 7',
              border: '1px solid var(--ms-border)',
              borderRadius: 'var(--ms-radius)',
            }}
          >
            {heroImageUrl ? (
              <Image src={heroImageUrl} alt={businessName} fill priority sizes="100vw" className="object-cover" />
            ) : heroVideoUrl ? (
              <video autoPlay muted loop playsInline className="absolute inset-0 w-full h-full object-cover" src={heroVideoUrl} />
            ) : null}
          </div>
        </div>
      )}
    </section>
  )
}
